// src/App.js
import React, { Suspense , useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';

import { initializeUsers } from './services/userService'; // Import original name here
import { initializeDefaultRoles } from './services/roleService'; // *** Import Role Initializer ***

// Lazy load pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const ReportsPage = React.lazy(() => import('./pages/reports/ReportsPage'));
const LoansListPage = React.lazy(() => import('./pages/loans/LoansListPage'));
const LoanDetailPage = React.lazy(() => import('./pages/loans/LoanDetailPage'));
const LoanEditPage = React.lazy(() => import('./pages/loans/LoanEditPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const PaymentsListPage = React.lazy(() => import('./pages/payments/PaymentsListPage'));
const PaymentDetailPage = React.lazy(() => import('./pages/payments/PaymentDetailPage'));
const PaymentEditPage = React.lazy(() => import('./pages/payments/PaymentEditPage')); // Use this for create/edit
const FeesListPage = React.lazy(() => import('./pages/fees/FeesListPage'));
const FeeEditPage = React.lazy(() => import('./pages/fees/FeeEditPage'));
const FeeDetailPage = React.lazy(() => import('./pages/fees/FeeDetailPage'));
const InvestorsListPage = React.lazy(() => import('./pages/investors/InvestorsListPage'));
const InvestorEditPage = React.lazy(() => import('./pages/investors/InvestorEditPage'));
const InvestorDetailPage = React.lazy(() => import('./pages/investors/InvestorDetailPage'));
const RemittanceListPage = React.lazy(() => import('./pages/remittances/RemittanceListPage'));
const RecordRemittancePage = React.lazy(() => import('./pages/remittances/RecordRemittancePage'));
const RemittanceDetailPage = React.lazy(() => import('./pages/remittances/RemittanceDetailPage'));

const CoaMappingListPage = React.lazy(() => import('./pages/coa/CoaMappingListPage'));
const CoaMappingEditPage = React.lazy(() => import('./pages/coa/CoaMappingEditPage'));
const JournalEntryPage = React.lazy(() => import('./pages/accounting/JournalEntryPage'));
const AuditLogPage = React.lazy(() => import('./pages/accounting/AuditLogPage'));
const ReconciliationPage = React.lazy(() => import('./pages/accounting/ReconciliationPage'));
const UserListPage = React.lazy(() => import('./pages/users/UserListPage'));
const UserEditPage = React.lazy(() => import('./pages/users/UserEditPage'));
const UserDetailPage = React.lazy(() => import('./pages/users/UserDetailPage'));
const RolesListPage = React.lazy(() => import('./pages/roles/RolesListPage')); // Optional
const RoleEditPage = React.lazy(() => import('./pages/roles/RoleEditPage')); // Optional

const BorrowersListPage = React.lazy(() => import('./pages/borrowers/BorrowersListPage'));
const BorrowerEditPage = React.lazy(() => import('./pages/borrowers/BorrowerEditPage'));
const BorrowerDetailPage = React.lazy(() => import('./pages/borrowers/BorrowerDetailPage'));
const LookupListPage = React.lazy(() => import('./pages/lookups/LookupListPage'));
const LookupEditPage = React.lazy(() => import('./pages/lookups/LookupEditPage'));
function App() {

  useEffect(() => {
    console.log("App Mounted: Initializing data...");
    initializeDefaultRoles(); // Roles first
    initializeUsers();      // Users second
  }, []); // Runs once on mount

  return (
    <AuthProvider>
      <Suspense fallback={<div className="vh-100 d-flex justify-content-center align-items-center">Loading Page...</div>}>
        <Routes>

          {/* Public Route */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected Routes inside Layout */}
          <Route element={<Layout />}>
            
            {/* Default redirect to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Dashboard */}
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager', 'staff', 'accountant']} />}>
              <Route path="dashboard" element={<DashboardPage />} />
            </Route>

            {/* Loans Module */}
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager', 'staff', 'accountant']} />}>
              <Route path="loans" element={<LoansListPage />} />
              <Route path="loans/detail/:id" element={<LoanDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager', 'staff']} />}>
              <Route path="loans/edit/:id" element={<LoanEditPage />} />
              <Route path="loans/create" element={<LoanEditPage />} />
            </Route>
            {/* Fees Module */}
            {/* Adjust roles as needed for viewing vs modifying */}
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager', 'accountant', 'staff']} />}>
               <Route path="fees" element={<FeesListPage />} />
               <Route path="fees/detail/:id" element={<FeeDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager']} />}> {/* More restricted for create/edit */}
               <Route path="fees/create" element={<FeeEditPage />} />
               <Route path="fees/edit/:id" element={<FeeEditPage />} />
            </Route>

            {/* Payments Module */}
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager', 'accountant']} />}>
              {/* <Route path="payments" element={<PaymentsListPage />} /> */}
              {/* <Route path="payments/detail/:id" element={<PaymentDetailPage />} /> */}
              <Route path="payments" element={<PaymentsListPage />} />
              <Route path="payments/detail/:id" element={<PaymentDetailPage />} />
              <Route path="payments/edit/:id" element={<PaymentEditPage />} />
              <Route path="payments/create" element={<PaymentEditPage />} />
            </Route>
            {/* Investors Module */}  
            {/* Adjust roles as necessary */}
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager', 'accountant']} />}>
                <Route path="investors" element={<InvestorsListPage />} />
                <Route path="investors/detail/:id" element={<InvestorDetailPage />} />
            </Route>
             <Route element={<ProtectedRoute allowedRoles={['admin', 'manager']} />}>
                <Route path="investors/create" element={<InvestorEditPage />} />
                <Route path="investors/edit/:id" element={<InvestorEditPage />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
               <Route path="lookups" element={<LookupListPage />} />
               <Route path="lookups/create" element={<LookupEditPage />} />
               <Route path="lookups/edit/:id" element={<LookupEditPage />} />
            </Route>
             {/* Remittances Module */}
             {/* Adjust roles as necessary */}
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager', 'accountant']} />}>
                <Route path="remittances" element={<RemittanceListPage />} />
                <Route path="remittances/detail/:id" element={<RemittanceDetailPage />} />
                <Route path="remittances/create" element={<RecordRemittancePage />} />
            </Route>
             {/* Accounting Functionality */}
             <Route element={<ProtectedRoute allowedRoles={['admin', 'accountant']} />}>
               <Route path="coa-mapping" element={<CoaMappingListPage />} />
               <Route path="journal-entries" element={<JournalEntryPage />} />
               <Route path="reconciliation" element={<ReconciliationPage />} />
               <Route path="audit-log" element={<AuditLogPage />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['admin']} />}> {/* Mapping Edit restricted */}
               <Route path="coa-mapping/create" element={<CoaMappingEditPage />} />
               <Route path="coa-mapping/edit/:id" element={<CoaMappingEditPage />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager', 'staff', 'accountant']} />}>
               <Route path="borrowers" element={<BorrowersListPage />} />
               <Route path="borrowers/detail/:id" element={<BorrowerDetailPage />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager']} />}> {/* Create/Edit more restricted */}
               <Route path="borrowers/create" element={<BorrowerEditPage />} />
               <Route path="borrowers/edit/:id" element={<BorrowerEditPage />} />
            </Route>
            {/* Reports Module */}
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager']} />}>
              <Route path="reports" element={<ReportsPage />} />
            </Route>

            {/* Settings Page */}
            <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
              {/* <Route path="settings" element={<SettingsPage />} /> */}
            </Route>
            <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
               <Route path="users" element={<UserListPage />} />
               <Route path="users/create" element={<UserEditPage />} />
               <Route path="users/edit/:id" element={<UserEditPage />} />
               <Route path="users/detail/:id" element={<UserDetailPage />} />
               <Route path="roles" element={<RolesListPage />} /> {/* Optional roles list */}
               <Route path="roles/create" element={<RoleEditPage />} />
               <Route path="roles/edit/:id" element={<RoleEditPage />} />
            </Route>
            {/* Catch-all inside layout */}
            <Route path="*" element={<NotFoundPage />} />

          </Route>

          {/* Catch-all outside layout */}
          <Route path="*" element={<NotFoundPage />} />

        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default App;
